import { useNavigate } from "react-router";
import { useHistory } from "../../../utils";
import { useNavigateDone } from "../../../hooks/useNavigateDone";
import { useGiftContext } from "../Context/GiftContextProvider";
import useGiftQuery from "./useGiftQuery";
import useOrderQuery from "./useOrderQuery";
import { useEffect, useState } from "react";
import { GiftInformation, PopulatedGift } from "../../../api/gift";
import { WizardStep } from "../../../consts/wizardSteps";

const useGiftHomePage = () => {
  const { search, replace } = useHistory();
  const { setKlydoGiftProps, klydoGiftProps, setSelectedKlydoGreet } =
    useGiftContext();
  const { data: orderInfo, isLoading: isLoadingOrderInfo } = useOrderQuery(
    (search.orderName || search.orderId) as string,
  );
  const { data: giftInfo, isLoading: isLoadingGift } = useGiftQuery({
    orderName:
      (search.basedOn as string) ||
      ((search.orderName || search.orderId) as string),
    clockId: search.clockId as string,
  });
  const gift: PopulatedGift = giftInfo?.gift;

  const [enterClockDialogOpen, setEnterClockDialogOpen] = useState(false);
  const [enterOrderNameDialogOpen, setEnterOrderNameDialogOpen] =
    useState(false);

  const navigate = useNavigate();
  const navigateToDone = useNavigateDone();

  const disabledCreateBasedOnGreet = search?.basedOn && !gift;

  useEffect(() => {
    if (search.basedOn && gift) {
      setKlydoGiftProps({
        klydoId: gift.klydoId,
        senderImageUrl: gift?.senderImageUrl,
        senderName: gift.senderName,
        date: gift?.date,
        greetingContent: gift.greetingContent,
        pendulumLogoUrl: gift?.pendulumLogoUrl,
      });
      setSelectedKlydoGreet(gift.klydo);
    }
  }, [search.basedOn, gift]);

  useEffect(() => {
    const orderName = search?.orderName || search?.orderId;
    const clockId = search?.clockId;
    let timeZone: string;
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {}
    setKlydoGiftProps({
      ...klydoGiftProps,
      orderName: orderName as string,
      clockId: clockId as string,
      timeZone,
    });
  }, []);

  const handleCreateGreet = () => {
    if ((search.basedOn && gift) || search.byOrder) {
      setEnterOrderNameDialogOpen(true);
      return;
    }
    if (
      !klydoGiftProps?.orderName &&
      !klydoGiftProps?.clockId &&
      !search.basedOn
    ) {
      setEnterClockDialogOpen(true);
      return;
    }

    navigate(`/gift/wizard?step=${WizardStep.OCCASION}`);
  };

  const handleCloseEnterClockDialog = () => {
    setEnterClockDialogOpen(false);
  };

  const prsonalInfo = orderInfo?.data?.first_name
    ? `${orderInfo?.data?.first_name} ${orderInfo?.data?.last_name}`
    : "";

  if (isLoadingOrderInfo || isLoadingGift) {
    return { isLoading: true };
  }

  if (gift && !search.basedOn) {
    setKlydoGiftProps(gift);
    setSelectedKlydoGreet(gift.klydo);
    if (giftInfo.clockInfo?.id) {
      navigateToDone({ clockId: giftInfo.clockInfo.id });
    } else if (gift.orderName) {
      navigateToDone({ orderName: gift.orderName });
    }
  }

  const handleClockConfirm = async (giftInformation: GiftInformation) => {
    if (giftInformation.gift) {
      setKlydoGiftProps(giftInformation.gift);
      setSelectedKlydoGreet(giftInformation.gift.klydo);
      navigateToDone({ clockId: giftInformation.clockInfo?.id });
      return;
    }
    setKlydoGiftProps({
      ...klydoGiftProps,
      clockId: giftInformation.clockInfo?.id,
    });
    navigate(`/gift/wizard?step=${WizardStep.OCCASION}`);
    setEnterClockDialogOpen(false);
  };

  const handleAddOrderName = async (
    giftInformation: GiftInformation,
    orderName: string,
  ) => {
    if (giftInformation?.gift) {
      setKlydoGiftProps(giftInformation.gift);
      setSelectedKlydoGreet(giftInformation.gift.klydo);
      navigateToDone({ orderName: giftInformation.gift.orderName });
      return;
    }
    setKlydoGiftProps({
      ...klydoGiftProps,
      orderName,
    });

    // basedOn is for B2B flows, so if we come from basedOn we want to go to personal greet step
    replace(
      `/gift/wizard?step=${search.basedOn ? WizardStep.PERSONAL_GREET : WizardStep.OCCASION}`,
    );
    setEnterOrderNameDialogOpen(false);
  };

  const handleCloseEnterOrderNameDialog = () => {
    setEnterOrderNameDialogOpen(false);
  };

  return {
    isLoading: false,
    handleCreateGreet,
    prsonalInfo,
    navigate,
    enterClockDialogOpen,
    handleCloseEnterClockDialog,
    handleClockConfirm,
    enterOrderNameDialogOpen,
    handleAddOrderName,
    handleCloseEnterOrderNameDialog,
    disabledCreateBasedOnGreet,
  };
};

export default useGiftHomePage;
